import { useCallback, useState } from 'react';

import { IPaymentDetails, RequestPaymentFn, paymentErrorType } from 'features/payment/types';

import {
  TransactionType,
  getGooglePaymentsConfigs,
} from 'state/google-pay/get-google-payments-configs';
import { useGooglePay } from 'state/google-pay/hooks/use-google-pay';
import { getConfigValue } from 'utils/environment';
import { ISOs, getCountryAndCurrencyCodes } from 'utils/form/constants';
import { logger } from 'utils/logger';

export const useRequestGooglePayPayment: RequestPaymentFn = ({ googlePay: googlePayInput }) => {
  const { isForPrepaidReload, billingCountry = ISOs.USA, reloadAmount, total } =
    googlePayInput ?? {};
  const [data, setData] = useState<IPaymentDetails>();
  const [error, setError] = useState<paymentErrorType>();

  const { requestingPayment: isLoading, requestGooglePayPayment } = useGooglePay({
    onError: error => {
      logger.error({
        message:
          '[useRequestGooglePayPayment][requestPayment] Error occurred while requesting GooglePay payment.',
        error,
      });

      setError(paymentErrorType.googlePayGeneric);
    },
    onCompleted: async googlePayDetails => {
      if (!googlePayDetails) {
        logger.warn({
          message: '[useRequestGooglePayPayment][requestPayment] User left the Google Pay Flow.',
        });
        return;
      }
      setData({ googlePayDetails });
    },
  });

  const requestPayment = useCallback(() => {
    const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
    const googleConfig = getConfigValue({ key: 'google', defaultValue: {} });
    // Get the checkout gateway configs for google pay
    const { gateway, gatewayMerchantId } = getGooglePaymentsConfigs({
      transactionType: isForPrepaidReload ? TransactionType.PREPAID : TransactionType.PAYMENT,
      countryCode,
      googleConfig,
    });

    requestGooglePayPayment({
      countryCode,
      currencyCode,
      total: isForPrepaidReload ? reloadAmount ?? 0 : total ?? 0,
      gatewayMerchantId,
      gateway,
    });
  }, [billingCountry, isForPrepaidReload, reloadAmount, requestGooglePayPayment, total]);

  return { data, isLoading, error, requestPayment };
};
